/**
 * The public API, documented from the same list the router is built from.
 *
 * `api/index.ts` is already "every write a signed-in person can make" — this
 * module does not add capability, it names what is already there so a script
 * has the same map a person reading the UI's network tab would have to
 * reconstruct by hand. If a route is added to the router and not here, this
 * page quietly describes an API smaller than the real one — which is worse
 * than a missing feature, because nothing fails loudly to say so. Keeping the
 * two lists next to each other in the same file is the whole defence; there
 * is no generator that could catch the two drifting apart on its own.
 *
 * Rendered by `GET /api/docs` (plain text, no auth needed — it is public by
 * design), by the app's public `/api-docs` page, and by
 * `npm run api-docs > docs/api.md`. One list, three readers.
 */

export interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  note: string;
}

export interface ApiEndpointGroup {
  title: string;
  blurb?: string;
  endpoints: ApiEndpoint[];
}

export const API_ENDPOINT_GROUPS: ApiEndpointGroup[] = [
  {
    title: 'API keys',
    blurb: 'Provisioning — see "Authentication" above for how a key is used.',
    endpoints: [
      { method: 'POST', path: '/keys', note: 'mint a personal API key — the plaintext is shown once' },
      { method: 'GET', path: '/keys', note: 'list your own keys (hint, label, last used — never the secret)' },
      { method: 'DELETE', path: '/keys/:id', note: 'revoke one of your own keys' },
    ],
  },
  {
    title: 'Tasks',
    blurb:
      'Everything the board and the composer do.\n\n' +
      'Questions a routine asks come back with `GET /tasks/:id` as `questions`, open ones first —\n' +
      'each with its `id`, `kind`, `question`, `context`, `options`, `allow_other` and, once answered,\n' +
      '`answer` and `answered_at`. Answer them with `questions/answer` (one answer per open `id`), or\n' +
      'simply reply: a comment from whoever raised the task, or an admin, while questions are open\n' +
      'answers all of them with its text (stored as "Other"), and the task leaves Needs you.',
    endpoints: [
      { method: 'POST', path: '/tasks', note: 'submit a task — the same call the composer makes; queued if no routine can take it yet' },
      { method: 'GET', path: '/projects/:id/tasks', note: 'list tasks, newest first (filter with ?status=&repo_id=&agent_id=&category_id=&kind=&origin=&limit=, where kind=none is the unclassified ones; ?queue=needs-you|awaiting-approval applies the board\'s own queue predicates server-side, and reads every open task, not the newest page)' },
      { method: 'GET', path: '/projects/:id/follow-ups', note: 'its follow-ups, suggested ones by default (?state=pending|suggested|accepted|rejected&limit=), each with the task it was suggested from' },
      { method: 'GET', path: '/tasks/:id', note: 'one task, in full, with the questions its routine asked (open ones first)' },
      { method: 'PATCH', path: '/tasks/:id', note: 'reclassify it: { kind } — bug, enhancement, feature, chore or null. Origin and quote are not editable' },
      { method: 'GET', path: '/tasks/:id/comments', note: 'its comment thread' },
      { method: 'GET', path: '/tasks/:id/events', note: 'its history panel' },
      { method: 'GET', path: '/tasks/:id/brief', note: 'the brief a routine would be handed, as text' },
      { method: 'POST', path: '/tasks/:id/comments', note: 'leave a comment (posted to the pull request too) — from the raiser or an admin, it also answers any open questions' },
      { method: 'POST', path: '/tasks/:id/questions/answer', note: 'answer the routine\'s open questions, all at once (posted to the pull request as one comment)' },
      { method: 'POST', path: '/follow-ups/:id/accept', note: 'turn a suggested follow-up into a task — optionally edited: title, prompt, auto_merge, smart, kind, agent_id' },
      { method: 'POST', path: '/follow-ups/:id/reject', note: 'take a suggested follow-up off the board' },
      { method: 'POST', path: '/tasks/:id/report', note: 'edit the same fields a routine reports' },
      { method: 'POST', path: '/tasks/:id/sign-off', note: '"looks right to me" — queues it for an admin' },
      { method: 'POST', path: '/tasks/:id/approve', note: 'admin-only: approve the merge — mid-work, turns auto-merge on instead' },
      { method: 'POST', path: '/tasks/:id/merged', note: 'mark it merged by hand' },
      { method: 'POST', path: '/tasks/:id/deployed', note: 'mark it deployed, optionally with a deploy_url' },
      { method: 'POST', path: '/tasks/:id/reopen', note: 'send it back for another pass' },
      { method: 'POST', path: '/tasks/:id/redispatch', note: 'retype the brief and send it to a routine again — queued if nobody is free' },
      { method: 'POST', path: '/tasks/:id/claim-code', note: 'admin-only: post a claim code on its pull request, for a session that lost its token' },
      { method: 'POST', path: '/tasks/:id/sync', note: 'ask GitHub for this pull request’s current state' },
      { method: 'DELETE', path: '/tasks/:id', note: 'move it to the trash (admin, or whoever raised it)' },
      { method: 'POST', path: '/tasks/:id/restore', note: 'undo a delete (admin, or whoever raised it)' },
      { method: 'POST', path: '/tasks/:id/snooze', note: 'park it — out of the working queues until it wakes' },
      { method: 'POST', path: '/tasks/:id/unsnooze', note: 'wake a snoozed task back up' },
      { method: 'GET', path: '/projects/:id/orphan-prs', note: 'open pull requests Rigg never started — local pull requests (no session link) are listed here and never auto-imported' },
      { method: 'POST', path: '/tasks/import', note: 'adopt one or more orphaned pull requests as tasks' },
      { method: 'POST', path: '/tasks/:id/hand-off', note: 'admin-only: give an adopted pull request nobody is on to a routine (optional agent_id, and an optional comment that leads the brief) — it continues on the existing branch, never opens a new pull request, and is reached by redispatch afterwards' },
      { method: 'POST', path: '/uploads', note: 'a signed URL to attach a file to a task or comment' },
    ],
  },
  {
    title: 'Projects & people',
    endpoints: [
      { method: 'GET', path: '/projects', note: 'every project you are a member of, with your role' },
      { method: 'GET', path: '/projects/:id', note: 'one project' },
      { method: 'GET', path: '/projects/:id/members', note: 'its roster' },
      { method: 'POST', path: '/projects', note: 'create a project (and its first repository)' },
      { method: 'PATCH', path: '/projects/:id', note: 'rename it' },
      { method: 'POST', path: '/projects/:id/invites', note: 'invite by email — joins on next sign-in' },
      { method: 'PATCH', path: '/projects/:id/members/:userId', note: 'change a member’s role' },
      { method: 'DELETE', path: '/projects/:id/members/:userId', note: 'remove a member' },
      { method: 'DELETE', path: '/invites/:id', note: 'withdraw a pending invitation' },
    ],
  },
  {
    title: 'Repositories',
    endpoints: [
      { method: 'GET', path: '/projects/:id/repos', note: 'every repository in the project' },
      { method: 'POST', path: '/repos', note: 'register a repository' },
      { method: 'PATCH', path: '/repos/:id', note: 'change its default branch, GitHub App installation or protected paths' },
      { method: 'DELETE', path: '/repos/:id', note: 'remove it' },
    ],
  },
  {
    title: 'Routines',
    blurb: '"Routine" and "agent" name the same thing — a dispatch target.',
    endpoints: [
      { method: 'GET', path: '/projects/:id/agents', note: 'every routine in the project' },
      { method: 'POST', path: '/agents', note: 'register a routine' },
      { method: 'PATCH', path: '/agents/:id', note: 'edit it, including its dispatch token' },
      { method: 'DELETE', path: '/agents/:id', note: 'remove it — its past tasks keep their history; refused (409) while a schedule is pinned to it' },
      { method: 'GET', path: '/projects/:id/dispatch-health', note: 'when the dispatch queue job last ran, and how it went' },
      { method: 'GET', path: '/projects/:id/subscriptions', note: 'the Claude subscriptions its routines run on — its own and those shared with it — with their usage' },
      { method: 'POST', path: '/subscriptions', note: 'add a subscription — optionally with its `claude setup-token` key, checked at once' },
      { method: 'PATCH', path: '/subscriptions/:id', note: 'rename it, set (null: remove) its key, or set `shared_project_ids`: the other projects that use it' },
      { method: 'DELETE', path: '/subscriptions/:id', note: 'remove it — its routines stay, unlinked' },
      { method: 'POST', path: '/subscriptions/:id/check', note: 'ask it for its limits now rather than at the hourly check' },
      { method: 'POST', path: '/projects/:id/agents/reset-rotation', note: 'put the dispatch rotation back to registration order' },
      { method: 'POST', path: '/agents/:id/tokens', note: 'mint an agent token for this routine — shown once' },
      { method: 'GET', path: '/agents/:id/tokens', note: 'list the agent tokens minted for it' },
      { method: 'DELETE', path: '/tokens/:id', note: 'revoke an agent token' },
    ],
  },
  {
    title: 'Categories',
    endpoints: [
      { method: 'GET', path: '/projects/:id/categories', note: 'every category in the project' },
      { method: 'POST', path: '/categories', note: 'create one (or resolve one by name)' },
      { method: 'PATCH', path: '/categories/:id', note: 'rename or recolor it' },
      { method: 'DELETE', path: '/categories/:id', note: 'remove it' },
    ],
  },
  {
    title: 'Settings',
    endpoints: [
      { method: 'GET', path: '/config', note: 'what the app needs that it cannot read out of Postgres' },
      { method: 'POST', path: '/projects/:id/github-token', note: 'set (or clear) the PAT fallback' },
      { method: 'POST', path: '/projects/:id/settings', note: 'standing instructions, deploy workflows and gate, and the approval-waiver check' },
      { method: 'GET', path: '/projects/:id/workflows', note: 'every GitHub Actions workflow its repositories define' },
      { method: 'GET', path: '/projects/:id/deploy', note: 'what a production deploy ships now, and its workflow\'s inputs (?repo_id=)' },
      { method: 'POST', path: '/projects/:id/deploy', note: 'admin-only: run the production workflow — { repo_id, inputs }' },
      { method: 'GET', path: '/projects/:id/schedules', note: 'its scheduled tasks, and what each one last did' },
      { method: 'POST', path: '/schedules', note: 'admin-only: open a task on a cron expression (UTC), through the same code as POST /tasks' },
      { method: 'PATCH', path: '/schedules/:id', note: 'edit one' },
      { method: 'DELETE', path: '/schedules/:id', note: 'remove one, and its cron job' },
    ],
  },
  {
    title: 'Intakes',
    blurb: 'Unauthenticated doors that open a task from outside Rigg — a form, a cron job.',
    endpoints: [
      { method: 'POST', path: '/intakes', note: 'create one (admin-only)' },
      { method: 'PATCH', path: '/intakes/:id', note: 'edit it' },
      { method: 'DELETE', path: '/intakes/:id', note: 'remove it' },
      { method: 'GET', path: '/intakes/:id/url', note: 'its URL and instructions, on demand' },
    ],
  },
];

function baseUrl(functionsUrl: string): string {
  return `${functionsUrl.replace(/\/+$/, '')}/api`;
}

/** The whole thing, as plain text — what `GET /api/docs` and the docs page render. */
export function apiDocsText(options: { functionsUrl: string; appUrl: string }): string {
  const out: string[] = [];
  const url = baseUrl(options.functionsUrl);

  out.push('# The Rigg API');
  out.push('');
  out.push(
    'Every write a signed-in person can make in the app, and the reads that make',
    'a script useful without a browser session — one router, documented from the',
    'same list it is built from, so this cannot describe an API the server does',
    'not actually have.',
  );
  out.push('');
  out.push(`Base URL: ${url}`);
  out.push('');
  out.push('## Authentication');
  out.push('');
  out.push(
    'Every call carries `Authorization: Bearer <token>`. Two kinds of token get',
    'you in, and they resolve to exactly the same thing — a profile and a role',
    'per project, checked on every route:',
    '',
    '  a Supabase session   what the browser holds after signing in — short-lived,',
    '                       refreshed automatically, never worth provisioning by hand',
    '  a personal API key   `rgk_…`, minted with `POST /keys`, worth exactly what',
    '                       your own session is worth and nothing more — it can',
    '                       reach the projects you belong to, with your role, and',
    '                       no others',
    '',
    'Only the SHA-256 of a key is stored, so a lost one is replaced, never',
    'recovered — mint a new one and revoke the old.',
  );
  out.push('');
  out.push('## The endpoints');

  for (const group of API_ENDPOINT_GROUPS) {
    out.push('', `### ${group.title}`, '');
    if (group.blurb) out.push(group.blurb, '');
    for (const endpoint of group.endpoints) {
      out.push(`    ${endpoint.method.padEnd(6)} ${endpoint.path.padEnd(38)} ${endpoint.note}`);
    }
  }

  out.push('');
  out.push('## Submitting a task from a script');
  out.push('');
  out.push(
    '```',
    `curl -X POST ${url}/tasks \\`,
    '  -H "Authorization: Bearer $RIGG_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    '  -d \'{',
    '    "project_id": "<project id>",',
    '    "prompt": "The lockfile is 3 months stale — bump it.",',
    '    "category": "Maintenance"',
    '  }\'',
    '```',
  );
  out.push('');
  out.push(
    'Besides `prompt`, the body takes `title`, `category`, `repo_id`, `agent_id`,',
    '`auto_merge` and `smart`, and three facts about where the work came from:',
    '',
    '  kind         optional; `bug`, `enhancement`, `feature` or `chore`',
    '  quote        optional, up to 4,000 characters; the requester\'s exact',
    '               words, verbatim. Shown to board members under the title and',
    '               never sent to a routine — not in the prompt, not in the',
    '               brief, not in any `/agent/*` response',
    '  origin       optional; the server decides where a task came from — a',
    '               session is `person`, an API key is `api_key`. The one value',
    '               a caller may assert is `brigg_issue` (anything else is a',
    '               400), and then `origin_ref` is required',
    '  origin_ref   with `origin`, up to 80 characters: the issue\'s handle, `#412`',
    '',
    'A task\'s `origin` is one of `person`, `api_key`, `brigg_issue`, `schedule`,',
    '`intake`, `follow_up`, `routine` or `import`, with `origin_ref` naming the',
    'schedule, intake, source task or routine where there is one.',
  );
  out.push('');
  out.push(
    'The response is the created task. Reads — the task list, its comments, its',
    'history — are plain `GET`s under `/projects/:id/tasks` and `/tasks/:id`,',
    'listed above.',
  );
  out.push('');
  out.push('## Scheduling a task');
  out.push('');
  out.push(
    '`POST /schedules` takes what `POST /tasks` takes, plus when. `PATCH` takes',
    'any of the same fields but `project_id`.',
    '',
    '  project_id   the project it opens tasks in',
    '  cron         five fields, evaluated in UTC — `0 6 * * 1` is Monday 06:00 UTC',
    '  title        what each task is called',
    '  prompt       handed to the routine as typed',
    '  category     optional; created if the project has not seen it',
    '  kind         optional; every task it opens is this kind',
    '  auto_merge   optional; every task it opens is approved from the outset',
    '  smart        optional; auto-pick a smart routine rather than a normal one',
    '  agent_id     optional; the routine every firing goes to, paused or not —',
    '               null (the default) is the rotation. It must be a routine in',
    '               the same project (403 otherwise, 404 if it does not exist).',
    '               A pinned schedule never falls back to the rotation: if the',
    '               routine cannot be reached the firing fails into `last_error`,',
    '               and the routine cannot be deleted while a schedule names it.',
    '  fire_and_forget',
    '               optional; every firing goes straight to `agent_id` with no',
    '               callback token, and the task kept for the record is',
    '               `background` — off the board, visible through the source',
    '               filter. Needs `agent_id` and cannot go with `auto_merge`',
    '               (400 either way).',
  );
  out.push('');
  out.push('## Protected paths');
  out.push('');
  out.push(
    '`PATCH /repos/:id` with `protected_paths` names the paths a task\'s',
    '`auto_merge` never waives approval for. Admins only; replaced whole, and',
    '`[]` (the default) protects nothing.',
    '',
    '  protected_paths  up to 100 globs: `*` matches within one path segment,',
    '                   `**` any depth — `.github/**` is every workflow',
    '',
    'When a pull request would merge on `auto_merge` alone and changes a file',
    'matching one, Rigg holds it for a person\'s approval and says which file and',
    'glob on the task (`merge_held_by`). An approval merges it as normal.',
    '',
    '```',
    `curl -X PATCH ${url}/repos/<repo id> \\`,
    '  -H "Authorization: Bearer $RIGG_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    '  -d \'{ "protected_paths": [".github/**", "scripts/ci/**"] }\'',
    '```',
  );
  out.push('');
  out.push(`The rest of what a routine itself needs — reporting, polling, the callback`);
  out.push(`protocol — is a different surface entirely, published at ${options.appUrl}/protocol.`);

  return out.join('\n');
}
